import { memo, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { NextComponentType } from 'next';
import { AppContext, AppInitialProps, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Router, { useRouter } from 'next/router';
import styled from 'styled-components';
import { BrowserMetrics, WebVital } from '@spotify-internal/semantic-metrics';
import { AppContextProvider } from '@context/AppContextProvider';
import { Layout } from '@components/Layout';
import { sendMetric, withRouteMetrics } from '@lib/semanticMetrics';
import '@spotify-internal/encore-web/css/fonts.css';
import '@lib/commandPalette/themes/encore.css';

const AppContainer = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const DisplayContainer = styled.div<{ shouldDisplay: boolean }>`
  display: ${props => (props.shouldDisplay ? 'block' : 'none')};
`;

type RouteCache = {
  [key: string]: {
    component: React.ReactElement;
    scrollPos: number;
  };
};

const ROUTES_TO_CACHE = ['/search'];

const release: string = process.env.NEXT_PUBLIC_SENTRY_VERSION || 'unknown';
// @TO-DO: Figure out how slingshot env is not being set on Tingle anymore
const isSlingShot: boolean = process.env.IS_SLINGSHOT === 'true';

if (process.env.NODE_ENV === 'production' && !isSlingShot) {
  Sentry.init({
    dsn: 'https://19e93cbef55a4f07bb8c7d5d5936a51f@o22381.ingest.sentry.io/6102758',
    release: release,
    allowUrls: [
      /musicproviders\.spotify\.com/,
      /atlas\.spotify\.com/, // production
      /atlas\.spotifycdn\.com/, // cdn
    ],
    denyUrls: [
      /atlas\.spotify\.com\/unauthorized/, // can be accessed without login by scrapers and bots
    ],
  });
}

const CustomApp: NextComponentType<AppContext, AppInitialProps, AppProps> = ({
  Component,
  pageProps,
}) => {
  const router = useRouter();
  const CACHED_ROUTES: RouteCache = {};
  const cachedComponents = useRef(CACHED_ROUTES);
  const isCacheableRoute = ROUTES_TO_CACHE.includes(
    router.asPath.replace(process.env.BASEPATH as string, ''),
  );

  const PageAnalytics = dynamic(
    () =>
      import('@spotify-internal/s4mp-page-analytics').then(
        mod => mod.PageAnalytics,
      ) as Promise<React.ComponentType<{}>>,
    { ssr: false },
  );

  // Add Component to cachedComponents if we haven't cached it already
  if (isCacheableRoute && !cachedComponents.current[router.asPath]) {
    const MemoComponent = memo(Component);
    cachedComponents.current[router.asPath] = {
      component: <MemoComponent {...pageProps} />,
      scrollPos: 0,
    };
  }

  // Save the scroll position of current page before leaving
  const handleRouteChangeStart = useCallback(() => {
    if (isCacheableRoute) {
      cachedComponents.current[router.asPath].scrollPos = window.scrollY;
    }
  }, [isCacheableRoute, router.asPath]);

  // Save scroll position - requires an up-to-date router.asPath
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.asPath, router.events, handleRouteChangeStart]);

  // Scroll to the saved position when we load a cached component
  useEffect(() => {
    if (isCacheableRoute) {
      window.scrollTo(0, cachedComponents.current[router.asPath].scrollPos);
    }
  }, [Component, pageProps, isCacheableRoute, router.asPath]);

  return (
    <AppContainer className="encore-creator-light-theme encore-layout-themes">
      <AppContextProvider>
        <PageAnalytics />
        <Layout>
          <DisplayContainer shouldDisplay={isCacheableRoute}>
            {/** TODO: Add e2e tests to cover cached navigation */}
            {Object.entries(cachedComponents.current).map(([path, cache]) => (
              <DisplayContainer
                key={path}
                shouldDisplay={router.asPath === path}
              >
                {cache.component}
              </DisplayContainer>
            ))}
          </DisplayContainer>
        </Layout>
        {!isCacheableRoute && <Component {...pageProps} />}
      </AppContextProvider>
    </AppContainer>
  );
};

export function reportWebVitals(metric: WebVital) {
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route: Router.router?.route ?? 'unknown',
    }),
  );
}

export default withRouteMetrics(CustomApp);
